<template>
	<Loading v-if="store.isLoading" />

	<Row class="pb-0 !pt-8 bg-white flex-col">
		<div class="container xl:container mx-auto">
			<img
				src="/images/ts-blog-image.webp"
				alt=""
				class="max-w-[900px] w-full mx-auto mb-8"
				loading="lazy"
				width="900"
				height="472"
			/>
		</div>
	</Row>
	<Row class="pb-0 pt-0 bg-white category-row">
		<div class="xl:container gap-[80px]">
			<div
				ref="cateogryDropdown"
				class="category-container"
			>
				<NuxtLink
					class="category-button"
					to="/blog/"
				>
					All
				</NuxtLink>
				<NuxtLink
					class="category-button"
					to="/category/guides/"
					data-slug="guides"
				>
					Guides
				</NuxtLink>
				<NuxtLink
					class="category-button"
					to="/category/success-stories/"
					data-slug="success-stories"
				>
					Success Stories
				</NuxtLink>
				<NuxtLink
					class="category-button"
					to="/category/patterns-to-short/"
					data-slug="patterns-to-short"
				>
					Patterns To Short
				</NuxtLink>
				<NuxtLink
					class="category-button"
					to="/category/lessons-from-students/"
					data-slug="lessons-from-students"
				>
					Lessons From Students
				</NuxtLink>
				<NuxtLink
					class="category-button"
					to="/category/mentor-update/"
					data-slug="mentor-update"
				>
					Mentor Updates
				</NuxtLink>
				<div class="category-dropdown-box">
					<button
						class="category-button more"
						:class="showMoreCategories ? 'active' : null"
						@click="openDropdown"
					>
						More
						<svg
							width="15"
							height="10"
							viewBox="0 0 15 10"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M2.145 0.499999L7.5 6.06275L12.855 0.5L14.5 2.21255L7.5 9.5L0.5 2.21255L2.145 0.499999Z"
								fill="#64748B"
							/>
						</svg>
					</button>

					<div
						v-if="showMoreCategories"
						class="category-dropdown active"
					>
						<NuxtLink
							v-for="itemCategory in categories"
							:key="itemCategory.id"
							class="category-button"
							:to="`/category/${itemCategory.slug}/`"
						>
							{{ itemCategory.name }}
						</NuxtLink>
					</div>
				</div>
			</div>
		</div>
	</Row>
	<Row>
		<div class="xl:container gap-[80px]">
			<section
				v-if="posts && posts.length > 0 && !store.isLoading"
				id="blog-list"
				class="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-[32px] px-[16px] md:px-[32px] xl:px-0"
			>
				<template
					v-for="(post, index) in posts"
					:key="post.id"
				>
					<PostTile
						:content="post.content.rendered"
						:source-url="post.image"
						:title="post.title.rendered"
						:slug="post.slug"
						:category="post.categories[0]"
						:data-page="post.page"
					/>

					<AdButlerGrid
						:ad-id="719238"
						v-if="(index + 1) % 5 === 0 && showAds"
						:data-page="post.page"
					/>
					<div
						v-else-if="(index + 1) % 5 === 0 && !showAds"
						class="ts-grid-fallback"
					></div>
				</template>
			</section>

			<section
				v-else
				class="h-[900px]"
			/>
		</div>
	</Row>

	<div
		ref="loadMoreAnchor"
		class="blog-loading-container"
	>
		<div class="post-loading">
			<img
				v-if="showLoad"
				src="/images/loading.svg"
				class="loading animation"
			/>
		</div>
	</div>
</template>

<script setup>
import categories from "~/utils/categories-data.json";

const showAds = ref(false);

const props = defineProps({
	posts: {
		type: Array,
		required: true
	},
	currentPage: {
		type: Number,
		required: true
	},
	totalPages: {
		type: Number,
		required: true
	},
	previousPage: {
		type: Number,
		required: true
	},
	category: {
		type: Object,
		required: false,
		default: null
	}
});
const config = useRuntimeConfig();

const $route = useRoute();

const API_ENDPOINT = `${config.public.hostname}wp-json/wp/v2/posts/`;
const showLoad = ref(false);
const loadMore = ref(false);
const cateogryDropdown = ref();
const loadMoreAnchor = ref();
const currentPage = ref(props.currentPage);
const posts = ref(props.posts);
const previousPage = ref(props.previousPage);
const showMoreCategories = ref(false);
const isPushStatePending = ref(false);
const route_name = ref("");

const isElementInViewport = (element) => {
	if (!element) {
		return false;
	}

	const rect = element.getBoundingClientRect();
	return rect.bottom > 0 && rect.top < window.innerHeight;
};

const loadMorePost = async () => {
	currentPage.value += 1;
	const { data } = await fetchPostsList(
		API_ENDPOINT,
		currentPage.value,
		props.category?.id
	);

	const posts_formatted = data.map((post) =>
		postShapeFormatter(post, currentPage.value)
	);

	posts.value = [...posts.value, ...posts_formatted];

	setTimeout(() => (loadMore.value = true), 250);

	if (!window.adsAreWithUs) {
		fetchFallbackAdbutlerBanner("ts-grid-ad", ".ts-grid-fallback");
	}
};

const loadMorePostBackwards = async () => {
	loadMore.value = false;
	const { data } = await fetchPostsList(
		API_ENDPOINT,
		previousPage.value,
		props.category?.id
	);

	if (data) {
		const posts_formatted = data.map((post) =>
			postShapeFormatter(post, previousPage.value)
		);

		posts.value = [...posts_formatted, ...posts.value];

		setTimeout(() => {
			const total_gap_per_row = 32 * 5;
			const total_tile_height_per_row = 292 * 5;
			const total_blog_header_space = 44 + 172;

			window.scrollTo(
				0,
				total_tile_height_per_row + total_gap_per_row + total_blog_header_space
			);
			previousPage.value -= 1;
			loadMore.value = true;
		}, 250);

		if (!window.adsAreWithUs) {
			fetchFallbackAdbutlerBanner("ts-grid-ad", ".ts-grid-fallback");
		}
	}
};

const isAtTop = () => {
	return window.scrollY === 0;
};

const handleVisibilityChange = () => {
	const visibilityCheck = isElementInViewport(loadMoreAnchor.value);

	if (isAtTop() && loadMore.value === true && previousPage.value !== 0) {
		loadMorePostBackwards();
	}

	if (
		currentPage.value <= props.totalPages &&
		visibilityCheck &&
		loadMore.value === true
		// &&this.currentPage != this.totalPages
	) {
		showLoad.value = true;
		loadMore.value = false;
		loadMorePost();
	}
};

const openDropdown = () => {
	showMoreCategories.value = !showMoreCategories.value;
};

const pageObserver = () => {
	const observer = new IntersectionObserver(
		function (entry) {
			if (entry[0].isIntersecting === true) {
				const page_num = Number(entry[0].target.getAttribute("data-page"));

				if (page_num && page_num > 0) {
					let url;

					const regex = /^\/blog\/page\/\d+\/$/;

					if (route_name === "/blog/" || regex.test(route_name)) {
						url = page_num === 1 ? "/blog/" : "/blog/page/" + page_num + "/";
					} else {
						let page_url = window.location.pathname;
						page_url = page_url.split("page")[0];

						url = page_num === 1 ? page_url : page_url + "page/" + page_num + "/";
					}

					if (!isPushStatePending.value) {
						history.pushState({}, null, url);
					}
					isPushStatePending.value = true;
					setTimeout(() => {
						isPushStatePending.value = false;
					}, 500);
				}
			}
		},
		{ threshold: [0] }
	);

	document.querySelectorAll(".post-tile").forEach((entry) => {
		observer.observe(entry);
	});
};

const scrollSuperThing = () => {
	// window.clearTimeout(isScrolling.value);
	// isScrolling.value = setTimeout(async () => {
	// }, 175);

	handleVisibilityChange();
	pageObserver();
};

watch(
	() => $route.name,
	(newPath, oldPath) => {
		if (process.client) {
			if (newPath !== oldPath) {
				window.removeEventListener("scroll", scrollSuperThing);
			}

			showAds.value = window.adsAreWithUs;
		}
	}
);

onMounted(() => {
	if (process.client) {
		if (window.adsAreWithUs) {
			showAds.value = window.adsAreWithUs;
		} else {
			fetchFallbackAdbutlerBanner("ts-grid-ad", ".ts-grid-fallback");
		}

		route_name.value = window.location.pathname;

		if (posts.value) {
			store.manualToggleLoading(false);
		}
		const container = cateogryDropdown.value;
		const button = container.querySelector(".category-button");

		if (!$route.params.slug) {
			button.classList.add("active");
		}

		window.addEventListener("scroll", scrollSuperThing, { passive: true });

		if (currentPage.value < props.totalPages) {
			loadMore.value = true;
		}
	}
});
</script>

<style lang="scss" scoped>
@import "../assets/utils.scss";

.category-row {
	@include breakpoint(lg) {
		position: sticky;
		top: 59px;
		z-index: 9;
	}
}

.router-link-exact-active {
	background-color: $primary-50;
	color: $primary-500 !important;
}

.category-button {
	display: inline-flex;
	padding: 12px 16px;
	justify-content: center;
	align-items: center;
	border: 1px solid $primary-50;
	color: $neutral-500;
	text-align: center;
	font-family: Rubik;
	font-size: 14px;
	font-style: normal;
	font-weight: 400;
	line-height: 100%;
	letter-spacing: 0.25px;
	gap: 8px;
	min-width: 96px;

	&.active {
		background-color: $primary-50;
		color: $primary-500;

		svg {
			transform: rotate(180deg);

			path {
				fill: $primary-500;
			}
		}
	}

	@include breakpoint(lg) {
		letter-spacing: 0.5px;

		padding: 12px 20px;
		font-size: 18px;
	}
}

.category-container {
	display: flex;
	gap: 12px;
	flex-wrap: wrap;
	padding: 0 16px;

	@include breakpoint(lg) {
		display: block;
		padding: 0;
	}
}

.category-dropdown-box {
	display: inline-block;

	button {
		background-color: $primary-50;
	}
}

.category-dropdown {
	display: flex;
	flex-wrap: wrap;
	gap: 12px;
}

.category-dropdown.active {
	padding: 12px 16px;
	position: absolute;
	left: 0;
	z-index: 1;
	width: 100vw;
	background-color: white;
	border-top: 1px solid $primary-500;
	// height: 200px;
	max-height: 320px;
	overflow-y: auto;
	max-width: 1200px;
	margin: auto;
	right: 0;

	.category-button {
		border-color: $neutral-300;
	}

	filter: drop-shadow(0px 12px 4px rgba(0, 0, 0, 0.2));

	@include breakpoint(lg) {
		border: 2px solid $primary-500;
		overflow-x: hidden;
		overflow-y: hidden;

		&:hover {
			overflow-y: auto;
		}

		border-radius: 8px;
	}
}

.category-button {
	border-color: $primary-50;
	text-align: left;
	padding: 12px 12px;

	&:hover {
		background-color: $primary-50;
		color: $primary-500;

		svg {
			path {
				fill: $primary-500;
			}
		}
	}

	@include breakpoint(lg) {
		padding-left: 16px;
		padding-right: 16px;
	}
}

.category-button.more.active {
	background-color: $primary-500;
	color: white;

	svg path {
		fill: white;
	}
}

.post-loading {
	padding: 64px 0;
	display: flex;
	justify-content: center;
}

.loading-gif {
	margin: auto;
	max-width: 64px;
}
</style>
